import { z } from 'zod';

/** Variables the app needs at build time (Vite exposes only `VITE_*`). */
export const appEnvSchema = z.object({
  VITE_SUPABASE_URL: z.url({ protocol: /^https?$/ }),
  VITE_SUPABASE_PUBLISHABLE_KEY: z.string().trim().min(1),
  // Google Picker (Vault's "Pick from Drive"). Optional: without them Drive picking is off.
  VITE_GOOGLE_PICKER_API_KEY: z
    .string()
    .trim()
    .optional()
    .transform((v) => v || undefined),
  VITE_GOOGLE_APP_ID: z
    .string()
    .trim()
    .optional()
    .transform((v) => v || undefined),
  // Web Push's public key (Phase 7 phone notifications). Optional: without it, "Notify this device" is off.
  VITE_VAPID_PUBLIC_KEY: z
    .string()
    .trim()
    .optional()
    .transform((v) => v || undefined),
  // Which Supabase project this build talks to; the app shows a DEV chip unless it's prod.
  VITE_HIVE_ENV: z
    .string()
    .trim()
    .optional()
    .transform((v): HiveEnvironment => (v === 'prod' ? 'prod' : 'dev')),
});

export type HiveEnvironment = 'dev' | 'prod';

/**
 * The env file a script or the MCP server reads: `.env` (hive-dev) normally, or
 * `.env.prod` when started with `--prod`. Prod values never go in `.env`.
 */
export function envFileFor(argv: readonly string[]): '.env' | '.env.prod' {
  return argv.includes('--prod') ? '.env.prod' : '.env';
}

export type AppEnv = z.infer<typeof appEnvSchema>;

export type EnvResult<T> = { ok: true; env: T } | { ok: false; problems: string[] };

/** Parses the app's env, listing every missing or bad variable rather than throwing. */
export function parseAppEnv(source: Record<string, unknown>): EnvResult<AppEnv> {
  const result = appEnvSchema.safeParse(source);
  if (result.success) return { ok: true, env: result.data };
  const problems = result.error.issues.map((issue) => {
    const key = issue.path.join('.');
    return source[key] == null || source[key] === '' ? `${key} is not set` : `${key} is not valid`;
  });
  return { ok: false, problems: [...new Set(problems)] };
}
